<script setup lang="ts">
/**
 * We can have several groups of statuses of different kinds:
 * - read file
 * - create file
 * - update file
 * - move file
 * - edit file
 * - delete file
 *
 * We want to split these into groups, ordered by the timestamp
 * of the first component/piece of them. and then grouped by the
 * request and response. We then display them in order, so that
 * they remain static and don't change order when new statuses are
 * added.
 */

const props = defineProps({
  status: null
})

/**
 * This works because maps maintain insertion order, so that's why we can
 * insert requests / responses into a map.
 */
const statuses = computed(() => {
  const reads: Map<string, {
    request: FileDownloadRequestDatagram
    response?: FileDownloadResponseDatagram
  }> = new Map()

  const creates: Map<string, {
    request: FileCreateRequestDatagram
    response?: FileCreateResponseDatagram
  }> = new Map()

  const moves: Map<string, {
    request: FileMoveRequestDatagram
    response?: FileMoveResponseDatagram
  }> = new Map()

  const updates: Map<string, {
    request: FileUpdateRequestDatagram
    response?: FileUpdateResponseDatagram
  }> = new Map()

  const edits: Map<string, {
    request: FileEditRequestDatagram
    response?: FileEditResponseDatagram
  }> = new Map()

  const copies: Map<string, {
    request: FileCopyRequestDatagram
    response?: FileCopyResponseDatagram
  }> = new Map()

  const searches: Map<string, {
    request: SearchListRequestDatagram
    response?: SearchListPageResponseDatagram
  }> = new Map()

  const ordered: Array<
    | { reads: typeof reads }
    | { creates: typeof creates }
    | { moves: typeof moves }
    | { updates: typeof updates }
    | { edits: typeof edits }
    | { copies: typeof copies }
    | { searches: typeof searches }
  > = []

  for (const part of props.status.parts) {
    if ("fileLoadRequested" in part) {
      if (reads.size === 0)
        ordered.push({ reads })
      const request = part.fileLoadRequested.request
      reads.set(request.requestId, { request })
    }
    else if ("fileLoadResponded" in part) {
      const response = part.fileLoadResponded.response
      const request = reads.get(response.requestId)
      if (request)
        request.response = response
      else
        console.error("Missing request for response", response)
    }
    else if ("fileCreateRequested" in part) {
      if (creates.size === 0)
        ordered.push({ creates })
      const request = part.fileCreateRequested.request
      creates.set(request.requestId, { request })
    }
    else if ("fileCreateResponded" in part) {
      const response = part.fileCreateResponded.response
      const request = creates.get(response.requestId)
      if (request)
        request.response = response
      else
        console.error("Missing request for response", response)
    }
    else if ("fileMoveRequested" in part) {
      if (moves.size === 0)
        ordered.push({ moves })
      const request = part.fileMoveRequested.request
      moves.set(request.requestId, { request })
    }
    else if ("fileMoveResponded" in part) {
      const response = part.fileMoveResponded.response
      const request = moves.get(response.requestId)
      if (request)
        request.response = response
      else
        console.error("Missing request for response", response)
    }
    else if ("fileUpdateRequested" in part) {
      if (updates.size === 0)
        ordered.push({ updates })
      const request = part.fileUpdateRequested.request
      updates.set(request.requestId, { request })
    }
    else if ("fileUpdateResponded" in part) {
      const response = part.fileUpdateResponded.response
      const request = updates.get(response.requestId)
      if (request)
        request.response = response
      else
        console.error("Missing request for response", response)
    }
    else if ("fileEditRequested" in part) {
      if (edits.size === 0)
        ordered.push({ edits })
      const request = part.fileEditRequested.request
      edits.set(request.requestId, { request })
    }
    else if ("fileEditResponded" in part) {
      const response = part.fileEditResponded.response
      const request = edits.get(response.requestId)
      if (request)
        request.response = response
      else
        console.error("Missing request for response", response)
    }
    else if ("fileCopyRequested" in part) {
      if (copies.size === 0)
        ordered.push({ copies })
      const request = part.fileCopyRequested.request
      copies.set(request.requestId, { request })
    }
    else if ("fileCopyResponded" in part) {
      const response = part.fileCopyResponded.response
      const request = copies.get(response.requestId)
      if (request)
        request.response = response
      else
        console.error("Missing request for response", response)
    }
    else if ("searchListRequested" in part) {
      if (searches.size === 0)
        ordered.push({ searches })
      const request = part.searchListRequested.request
      searches.set(request.requestId, { request })
    }
    else if ("searchListResponded" in part) {
      const response = part.searchListResponded.response
      const request = searches.get(response.requestId)
      if (request)
        request.response = response
      else
        console.error("Missing request for response", response, props.status.parts)
    }
  }

  return ordered
})
</script>

<template>
  <div class="panel-agent-status-container">
    <template v-for="(group, idx) in statuses" :key="idx">
      <template v-if="'reads' in group">
        <agent-status-reads :reads="group.reads" />
      </template>
      <template v-else-if="'creates' in group">
        <agent-status-creates :creates="group.creates" />
      </template>
      <template v-else-if="'updates' in group">
        <agent-status-updates :updates="group.updates" />
      </template>
      <template v-else-if="'moves' in group">
        <agent-status-moves :moves="group.moves" />
      </template>
      <template v-else-if="'edits' in group">
        <agent-status-edits :edits="group.edits" />
      </template>
      <template v-else-if="'copies' in group">
        <agent-status-copies :copies="group.copies" />
      </template>
      <template v-else-if="'searches' in group">
        <agent-status-search
          v-for="[key, { request, response }] in group.searches"
          :key
          :request
          :response
        />
      </template>
    </template>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.panel-agent-status-container
  @apply px-[#{$panel-p-default}] inline-flex flex-col gap-1.5 w-full
</style>
